import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useCompanyStore, useBlogCategoriesStore } from 'stores';
import { loadCompanyMetadata } from 'utils/loadCompanyMetadata';
import { normalizeLanguageCode } from 'utils/apiLanguage';
import { resolveContentLanguage, DEFAULT_LOCALE } from 'utils/localePath';

const LanguageReloadContext = createContext({ contentLanguage: 'es' });

export const useLanguageReload = () => useContext(LanguageReloadContext);

const resolveTargetLanguage = (pathname) => resolveContentLanguage(pathname);

export const LanguageReloadProvider = ({ children }) => {
  const location = useLocation();
  const { i18n: i18nInstance } = useTranslation();
  const loadMetaCompany = useCompanyStore((state) => state.loadMetaCompany);
  const theme = useCompanyStore((state) => state.theme);
  const fetchBlogCategories = useBlogCategoriesStore((state) => state.fetchBlogCategories);

  const activeLanguage = resolveTargetLanguage(location.pathname);

  const [contentLanguage, setContentLanguage] = useState(() => activeLanguage);
  const previousLanguage = useRef(activeLanguage);
  const isInitialSync = useRef(true);
  const syncInProgress = useRef(false);

  useEffect(() => {
    const language = resolveTargetLanguage(location.pathname);
    let cancelled = false;

    const sync = async () => {
      if (syncInProgress.current) return;
      const languageChanged = previousLanguage.current !== language;

      if (!languageChanged && !isInitialSync.current) return;

      syncInProgress.current = true;

      try {
        const currentLanguage = normalizeLanguageCode(i18n.language) || DEFAULT_LOCALE;
        const needsLanguageChange = currentLanguage !== language;

        if (needsLanguageChange) {
          await i18n.changeLanguage(language);
        }

        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('i18nextLng', language);
        }

        if (cancelled) return;

        if (languageChanged || isInitialSync.current) {
          await loadCompanyMetadata(loadMetaCompany, theme);
          await fetchBlogCategories();
        }

        if (cancelled) return;

        isInitialSync.current = false;
        previousLanguage.current = language;
        setContentLanguage(language);
      } finally {
        syncInProgress.current = false;
      }
    };

    sync();

    return () => {
      cancelled = true;
    };
  }, [activeLanguage, loadMetaCompany, theme, fetchBlogCategories]);

  return (
    <LanguageReloadContext.Provider value={{ contentLanguage }}>
      {children}
    </LanguageReloadContext.Provider>
  );
};
